import { Injectable, computed, signal } from '@angular/core';

import { TicketDetail } from '../models/ticket.model';
import {
  AutomationMatch,
  AutomationScope,
  AutomationSuggestionTicket,
  AutomationValue,
  TicketAutomationRule,
  TicketAutomationSnapshot,
  TicketAutomationSuggestion,
} from './ticket-automation.model';
import { TICKET_AUTOMATION_RULES } from './ticket-automation.rules';

/**
 * Teto da fila de sugestões. Existe só como rede: com o dedupe por chave e a poda por
 * escopo a fila não passa de um ou dois itens na prática.
 */
const MAX_QUEUE = 20;

/**
 * Chave da API (as que a manutenção em massa escreve) → dot-path do modelo de leitura.
 *
 * Só o caminho em lote precisa disto: lá não existe estado "antes" por chamado, então o que
 * define "mudou" é a chave ter sido escrita no payload do lote.
 */
const API_KEY_TO_PATH: Record<string, string> = {
  ticket_status_id: 'status.id',
  system_id: 'system.id',
  ticket_type_id: 'ticket_type.id',
  customer_id: 'customer.id',
  type: 'type',
  situation: 'situation',
  urgent: 'urgent',
  priority: 'priority',
  expected_date: 'expected_date',
};

/**
 * Motor das automações do chamado: casa as regras de `TICKET_AUTOMATION_RULES` contra o que
 * acabou de ser salvo e enfileira as sugestões que o `TicketAutomationConfirmComponent` exibe.
 *
 * Garantias de isolamento — nenhum fluxo de save pode ser travado por aqui:
 *  - `evaluate`/`evaluateBatch` devolvem `void`: ninguém aguarda;
 *  - o corpo roda em `queueMicrotask`, FORA da call stack do save;
 *  - tudo dentro de try/catch: regra malformada vira `console.warn`, nunca erro na tela.
 *
 * `providedIn: 'root'` e de propósito NÃO injeta `TicketService` (que é provido na rota
 * `v3ndor`, fora do alcance de um serviço root) — o PATCH da sugestão fica no componente.
 */
@Injectable({ providedIn: 'root' })
export class TicketAutomationService {
  private readonly rules: TicketAutomationRule[] = TICKET_AUTOMATION_RULES;

  private readonly queue = signal<TicketAutomationSuggestion[]>([]);

  /** Somente leitura, para testes e depuração. */
  readonly suggestions = this.queue.asReadonly();

  /**
   * Todos os dot-paths que alguma regra observa. É o que o `snapshot` copia — o custo do
   * mecanismo no caminho normal é proporcional a isto, não ao tamanho do chamado.
   */
  private readonly watchedPaths = computed<string[]>(() => {
    const paths = new Set<string>();
    for (const rule of this.rules) {
      for (const criteria of [rule.when, rule.changedTo, rule.alreadyApplied]) {
        if (criteria) Object.keys(criteria).forEach(p => paths.add(p));
      }
    }
    return [...paths];
  });

  // ─── Snapshot ────────────────────────────────────────────────────────────────

  /**
   * Fotografa os caminhos observados ANTES do save.
   *
   * Tem de ser um objeto novo: `persistField` e `persistBulkEdit` fazem
   * `Object.assign(this.ticket, updatedModel)` — mutam o mesmo objeto que a tela guarda. Sem
   * o snapshot, o "antes" viraria o "depois" e nenhum gatilho detectaria mudança.
   */
  snapshot(ticket: TicketDetail | null | undefined): TicketAutomationSnapshot {
    const snap: TicketAutomationSnapshot = {};
    if (!ticket) return snap;
    for (const path of this.watchedPaths()) {
      snap[path] = TicketAutomationService.getPath(ticket, path);
    }
    return snap;
  }

  // ─── Avaliação ───────────────────────────────────────────────────────────────

  /**
   * Caminho unitário: um chamado que acabou de ser salvo. Fire-and-forget.
   *
   * @param before snapshot tirado antes do save (`snapshot()`)
   * @param after  estado do chamado depois do save
   */
  evaluate(
    before: TicketAutomationSnapshot,
    after: TicketDetail | null | undefined,
    scope: AutomationScope = 'ticket-detail'
  ): void {
    queueMicrotask(() => {
      try {
        if (!after) return;
        // Sem estado anterior não existe "mudou": avaliar aqui produziria sugestão falsa na
        // primeira carga da tela, onde tudo parece ter acabado de ganhar valor.
        if (!before || Object.keys(before).length === 0) return;

        for (const rule of this.matchRules(before, after)) {
          this.enqueue({
            key: `${rule.id}|${scope}|${after.id}`,
            rule,
            scope,
            tickets: [TicketAutomationService.toSuggestionTicket(after)],
          });
        }
      } catch (e) {
        console.warn('[ticket-automation] falha ao avaliar automações (save não afetado):', e);
      }
    });
  }

  /**
   * Caminho da manutenção em massa. Agrupa os chamados que casaram numa sugestão por regra —
   * é esse agrupamento que alimenta a lista de números de chamado no modal.
   *
   * @param tickets        os `TicketDetail` devolvidos em `BulkMaintenanceItemResult.ticket`
   * @param changedApiKeys chaves de `BulkMaintenanceFields` efetivamente escritas no lote
   */
  evaluateBatch(
    tickets: TicketDetail[],
    changedApiKeys: string[],
    scope: AutomationScope = 'bulk'
  ): void {
    queueMicrotask(() => {
      try {
        if (!tickets?.length || !changedApiKeys?.length) return;

        const changedPaths = new Set(
          changedApiKeys.map(k => API_KEY_TO_PATH[k]).filter((p): p is string => !!p)
        );
        if (!changedPaths.size) return;

        for (const rule of this.rules) {
          if (rule.enabled === false) continue;
          if (!TicketAutomationService.triggersOnPaths(rule, changedPaths)) continue;

          const matched = tickets.filter(
            t =>
              !!t &&
              TicketAutomationService.matchesAll(t, rule.when) &&
              TicketAutomationService.matchesAll(t, rule.changedTo) &&
              !TicketAutomationService.matchesAll(t, rule.alreadyApplied)
          );
          if (!matched.length) continue;

          const ids = matched.map(t => t.id).sort();
          this.enqueue({
            key: `${rule.id}|${scope}|${ids.join(',')}`,
            rule,
            scope,
            tickets: matched.map(TicketAutomationService.toSuggestionTicket),
          });
        }
      } catch (e) {
        console.warn('[ticket-automation] falha ao avaliar automações do lote:', e);
      }
    });
  }

  /**
   * Regras que casam com esta alteração. Síncrono e puro — exposto para os testes cobrirem o
   * casamento sem depender do microtask.
   */
  matchRules(before: TicketAutomationSnapshot, after: TicketDetail): TicketAutomationRule[] {
    return this.rules.filter(rule => {
      if (rule.enabled === false) return false;
      // 1. pré-requisito
      if (!TicketAutomationService.matchesAll(after, rule.when)) return false;
      // 2. gatilho: mudou E assumiu o valor esperado
      if (!TicketAutomationService.hasTriggered(before, after, rule.changedTo)) return false;
      // 3. já está no estado sugerido → não incomoda
      if (TicketAutomationService.matchesAll(after, rule.alreadyApplied)) return false;
      return true;
    });
  }

  // ─── Fila ────────────────────────────────────────────────────────────────────

  /**
   * A sugestão que este host deve exibir.
   *
   * Seleciona por escopo/chamado em vez de devolver a cabeça da fila: uma sugestão órfã (o
   * usuário salvou e fechou a tela sem responder) não pode esconder as seguintes.
   */
  pendingFor(scope: AutomationScope, ticketId?: string | null): TicketAutomationSuggestion | null {
    return (
      this.queue().find(s => {
        if (s.scope !== scope) return false;
        if (scope !== 'ticket-detail' || !ticketId) return true;
        return s.tickets.some(t => t.id === ticketId);
      }) ?? null
    );
  }

  /** Remove a sugestão da fila — tanto no confirmar quanto no cancelar. */
  resolve(key: string): void {
    this.queue.update(q => q.filter(s => s.key !== key));
  }

  /** Descarta sugestões órfãs. Chamado no `ngOnDestroy` do host de dialog. */
  discardFor(scope: AutomationScope, ticketId?: string | null): void {
    this.queue.update(q =>
      q.filter(s => {
        if (s.scope !== scope) return true;
        if (scope === 'ticket-detail' && ticketId) return !s.tickets.some(t => t.id === ticketId);
        return false;
      })
    );
  }

  private enqueue(suggestion: TicketAutomationSuggestion): void {
    this.queue.update(q => {
      if (q.some(s => s.key === suggestion.key)) return q;

      // Poda: no escopo de detalhe, sugestão de OUTRO chamado é resíduo de uma tela que já
      // foi fechada — sai da fila em vez de ficar ocupando espaço.
      const kept =
        suggestion.scope === 'ticket-detail'
          ? q.filter(
              s =>
                s.scope !== 'ticket-detail' ||
                s.tickets.some(t => suggestion.tickets.some(nt => nt.id === t.id))
            )
          : q;

      const next = [...kept, suggestion];
      return next.length > MAX_QUEUE ? next.slice(next.length - MAX_QUEUE) : next;
    });
  }

  // ─── Helpers puros ───────────────────────────────────────────────────────────

  /** Leitura por dot-path (`status.id`). */
  private static getPath(obj: unknown, path: string): unknown {
    return path
      .split('.')
      .reduce<unknown>(
        (acc, key) =>
          acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined,
        obj
      );
  }

  private static isEmpty(value: unknown): boolean {
    return value === null || value === undefined || value === '';
  }

  /**
   * Compara um valor com o esperado. `null` no critério significa "vazio" e casa com
   * `null`/`undefined`/`''` — é o que faz `alreadyApplied: { type: null }` funcionar, já que
   * o backend pode devolver qualquer um dos três para um Tipo em branco.
   *
   * A comparação por `String()` cobre id/número que chega como texto de um lado e número do
   * outro, sem precisar duplicar a constante na regra.
   */
  private static matchesValue(value: unknown, expected: AutomationValue): boolean {
    if (expected === null) return TicketAutomationService.isEmpty(value);
    if (TicketAutomationService.isEmpty(value)) return false;
    return value === expected || String(value) === String(expected);
  }

  private static matchesCriterion(
    value: unknown,
    expected: AutomationValue | AutomationValue[]
  ): boolean {
    return Array.isArray(expected)
      ? expected.some(e => TicketAutomationService.matchesValue(value, e))
      : TicketAutomationService.matchesValue(value, expected);
  }

  /** Verdadeiro só se TODOS os critérios casarem. Critério ausente/vazio → `false`. */
  private static matchesAll(ticket: TicketDetail, criteria?: AutomationMatch): boolean {
    if (!criteria) return false;
    const entries = Object.entries(criteria);
    if (!entries.length) return false;
    return entries.every(([path, expected]) =>
      TicketAutomationService.matchesCriterion(
        TicketAutomationService.getPath(ticket, path),
        expected
      )
    );
  }

  /** Gatilho unitário: cada caminho precisa ter mudado E assumido o valor esperado. */
  private static hasTriggered(
    before: TicketAutomationSnapshot,
    after: TicketDetail,
    changedTo: AutomationMatch
  ): boolean {
    const entries = Object.entries(changedTo);
    if (!entries.length) return false;
    return entries.every(([path, expected]) => {
      const now = TicketAutomationService.getPath(after, path);
      if (before[path] === now) return false;
      return TicketAutomationService.matchesCriterion(now, expected);
    });
  }

  /** Gatilho em lote: todos os caminhos do gatilho foram escritos pelo lote. */
  private static triggersOnPaths(rule: TicketAutomationRule, changedPaths: Set<string>): boolean {
    const paths = Object.keys(rule.changedTo);
    return paths.length > 0 && paths.every(p => changedPaths.has(p));
  }

  private static toSuggestionTicket(ticket: TicketDetail): AutomationSuggestionTicket {
    return {
      id: ticket.id,
      number: String(ticket.ticket_number_teorema ?? ticket.ref ?? ticket.id),
      summary: ticket.summary ?? null,
      customer: ticket.customer_name ?? null,
    };
  }
}
